import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/admin/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { 
  useFleetProviders, 
  useCreateFleetProvider, 
  useUpdateFleetProvider, 
  useDeleteFleetProvider,
  FleetProvider 
} from "@/hooks/use-fleet-providers";
import { ProviderFormSheet } from "@/components/fleet/ProviderFormSheet";
import { 
  Plus, 
  Building2, 
  Phone, 
  Mail, 
  MapPin, 
  Star,
  Edit, 
  Eye, 
  Trash2,
  MoreHorizontal 
} from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { ResponsiveActionMenu } from "@/components/ui/responsive-action-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFleetProviderTypes } from "@/hooks/use-fleet-provider-types";

export default function FleetProviders() {
  const { profile, hasCompanyRole, activeCompany } = useAuth();
  const { canCreate, canEdit, canDelete, isViewer } = useFleetPermissions();
  const { toast } = useToast();
  const [selectedProvider, setSelectedProvider] = useState<FleetProvider | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  // Get current user's company
  const { data: userCompanies } = useQuery({
    queryKey: ["userCompanies"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_companies")
        .select("company_id, companies(name)")
        .eq("user_id", profile?.id);
      return data || [];
    },
    enabled: !!profile?.id
  });

  const currentCompanyId = userCompanies?.[0]?.company_id;

  // Fetch providers using the new hook
  const { data: providers = [] } = useFleetProviders(currentCompanyId);
  
  // Fetch provider types from database
  const { data: providerTypesFromDb = [] } = useFleetProviderTypes(currentCompanyId);

  // Mutations
  const createProviderMutation = useCreateFleetProvider(currentCompanyId || "");
  const updateProviderMutation = useUpdateFleetProvider(currentCompanyId || "");
  const deleteProviderMutation = useDeleteFleetProvider(currentCompanyId || "");

  // Handlers
  const handleCreateProvider = (data: any) => {
    createProviderMutation.mutate(data, {
      onSuccess: () => setIsCreateOpen(false)
    });
  };

  const handleUpdateProvider = (data: any) => {
    updateProviderMutation.mutate(data, {
      onSuccess: () => {
        setIsEditOpen(false);
        setSelectedProvider(null);
      }
    });
  };

  const handleEditProvider = (provider: FleetProvider) => {
    setSelectedProvider(provider);
    setIsEditOpen(true);
  };

  const handleDeleteProvider = (providerId: string) => {
    // Verificar permissão primeiro
    if (!hasCompanyRole(activeCompany?.id, 'company_admin')) {
      toast({
        title: "Erro ao excluir fornecedor",
        description: "Você não tem permissão para excluir fornecedores. Apenas administradores da empresa podem realizar esta ação.",
        variant: "destructive"
      });
      return;
    }
    deleteProviderMutation.mutate(providerId);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: "default",
      inactive: "secondary",
      blacklisted: "destructive"
    } as const;
    
    const labels = {
      active: "Ativo",
      inactive: "Inativo", 
      blacklisted: "Lista Negra"
    };
    
    return <Badge variant={variants[status as keyof typeof variants] || "outline"}>
      {labels[status as keyof typeof labels] || status}
    </Badge>;
  };

  const getTypeLabel = (type: string) => {
    const providerType = providerTypesFromDb.find(t => t.code === type);
    return providerType?.name || type;
  };

  const renderRating = (rating?: number) => {
    if (!rating) return "N/A";
    return (
      <div className="flex items-center gap-1">
        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        <span>{rating.toFixed(1)}</span>
      </div>
    );
  };

  const columns: ColumnDef<FleetProvider>[] = [
    {
      accessorKey: "name",
      header: "Nome",
    },
    {
      accessorKey: "type",
      header: "Tipo",
      cell: ({ row }) => (
        <Badge variant="outline">{getTypeLabel(row.getValue("type"))}</Badge>
      ),
    },
    {
      accessorKey: "cnpj",
      header: "CNPJ",
      cell: ({ row }) => row.getValue("cnpj") || "N/A",
    },
    {
      accessorKey: "contact_info",
      header: "Contato",
      cell: ({ row }) => {
        const contactInfo = row.getValue("contact_info") as any;
        return (
          <div className="space-y-1">
            {contactInfo?.phone && (
              <div className="flex items-center gap-1 text-sm">
                <Phone className="h-3 w-3" />
                {contactInfo.phone}
              </div>
            )}
            {contactInfo?.email && (
              <div className="flex items-center gap-1 text-sm">
                <Mail className="h-3 w-3" />
                {contactInfo.email}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "address",
      header: "Localização",
      cell: ({ row }) => {
        const address = row.getValue("address") as any;
        if (address?.city && address?.state) {
          return `${address.city}, ${address.state}`;
        }
        return "N/A";
      },
    },
    {
      accessorKey: "rating",
      header: "Avaliação",
      cell: ({ row }) => renderRating(row.getValue("rating")),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getStatusBadge(row.getValue("status")),
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => (
        <ResponsiveActionMenu
          trigger={
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          }
          title="Ações do Fornecedor"
          align="end"
        >
          {canEdit && (
            <DropdownMenuItem onClick={() => handleEditProvider(row.original)}>
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem 
              onClick={() => handleDeleteProvider(row.original.id)}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir
            </DropdownMenuItem>
          )}
        </ResponsiveActionMenu>
      ),
    },
  ];

  // Filter providers based on active tab
  const filteredProviders = providers.filter(provider => {
    if (activeTab === "all") return true;
    if (activeTab === "active") return provider.status === "active";
    if (activeTab === "inactive") return provider.status === "inactive";
    return provider.type === activeTab;
  });

  // Summary data
  const totalProviders = providers.length;
  const activeProviders = providers.filter(p => p.status === "active").length;
  const workshopProviders = providers.filter(p => p.type === "workshop").length;
  const gasStationProviders = providers.filter(p => p.type === "gas_station").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fornecedores</h1>
          <p className="text-muted-foreground">
            Gerencie fornecedores de serviços da frota
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Button disabled={!canCreate} onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Fornecedor
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Fornecedores</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProviders}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ativos</CardTitle>
            <Building2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeProviders}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Oficinas</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{workshopProviders}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Postos</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{gasStationProviders}</div>
          </CardContent>
        </Card>
      </div>

      {/* Providers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Fornecedores</CardTitle>
          <CardDescription>
            Visualize e gerencie todos os fornecedores de serviços
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="active">Ativos</TabsTrigger>
              {providerTypesFromDb
                .filter(type => type.is_active)
                .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                .map(type => (
                  <TabsTrigger key={type.code} value={type.code}>
                    {type.name}
                  </TabsTrigger>
                ))}
            </TabsList>
            <TabsContent value={activeTab} className="space-y-4">
              <DataTable columns={columns} data={filteredProviders} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modals */}
      <ProviderFormSheet
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={handleCreateProvider}
        isLoading={createProviderMutation.isPending}
        companyId={currentCompanyId || ""}
      />

      <ProviderFormSheet
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open);
          if (!open) setSelectedProvider(null);
        }}
        onSubmit={handleUpdateProvider}
        provider={selectedProvider}
        isLoading={updateProviderMutation.isPending}
        companyId={currentCompanyId || ""}
      />
    </div>
  );
}